import { io, type Socket } from 'socket.io-client'

type SocketEventHandler<T = unknown> = (payload: T) => void

const socketUrl = import.meta.env.VITE_SOCKET_URL || import.meta.env.VITE_WS_LINK || import.meta.env.VITE_CSAPI_URL || undefined
const socketTransports = String(import.meta.env.VITE_SOCKET_TRANSPORTS || '')
  .split(',')
  .map((transport) => transport.trim())
  .filter(Boolean)

class AppSocketService {
  private socket: Socket | null = null
  private rooms = new Map<string, number>()
  private handlers = new Map<string, Set<SocketEventHandler>>()

  connect(token: string) {
    if (!token) {
      this.disconnect()
      return
    }

    if (!this.socket) {
      this.socket = io(socketUrl, {
        auth: { token },
        autoConnect: false,
        ...(socketTransports.length ? { transports: socketTransports } : {}),
      })
      this.socket.on('connect', () => this.rejoinRooms())
      this.attachHandlers()
    } else {
      this.socket.auth = { token }
    }

    if (!this.socket.connected) this.socket.connect()
  }

  disconnect() {
    this.rooms.clear()
    if (!this.socket) return
    this.socket.disconnect()
    this.socket.removeAllListeners()
    this.socket = null
  }

  on<T = unknown>(eventName: string, handler: SocketEventHandler<T>) {
    const handlers = this.handlers.get(eventName) || new Set<SocketEventHandler>()
    handlers.add(handler as SocketEventHandler)
    this.handlers.set(eventName, handlers)
    this.socket?.on(eventName, handler)
  }

  off<T = unknown>(eventName: string, handler: SocketEventHandler<T>) {
    const handlers = this.handlers.get(eventName)
    handlers?.delete(handler as SocketEventHandler)
    if (handlers?.size === 0) this.handlers.delete(eventName)
    this.socket?.off(eventName, handler)
  }

  joinRoom(room: string) {
    if (!room) return
    const count = this.rooms.get(room) || 0
    this.rooms.set(room, count + 1)
    if (!count) this.emitJoinRoom(room)
  }

  leaveRoom(room: string) {
    if (!room) return
    const count = this.rooms.get(room) || 0
    if (count > 1) { this.rooms.set(room, count - 1); return }
    if (!count) return
    this.rooms.delete(room)
    this.socket?.emit('leave-room', room)
  }

  announce(room: string, event: string, data: unknown) {
    const token = localStorage.getItem('jwt') || localStorage.getItem('token') || ''
    // Do not queue stale match context while offline; reconnect announces the current selection.
    if (token && this.socket?.connected) this.socket.emit(event, { room, data, token })
  }

  pairTV(room: string, code: string) {
    const token = localStorage.getItem('jwt') || localStorage.getItem('token') || ''
    if (!token || !this.socket?.connected) return false
    this.socket.emit('PAIR', { room, msg: code, token })
    return true
  }

  private rejoinRooms() {
    for (const room of this.rooms.keys()) this.emitJoinRoom(room)
  }

  private attachHandlers() {
    if (!this.socket) return
    for (const [eventName, handlers] of this.handlers.entries()) {
      for (const handler of handlers) this.socket.on(eventName, handler)
    }
  }

  private emitJoinRoom(room: string) {
    const token = localStorage.getItem('jwt') || localStorage.getItem('token') || ''
    this.socket?.emit('join-room', room, token)
  }
}

export const appSocket = new AppSocketService()
